import OpenInNewIcon from '@material-design-icons/svg/filled/open_in_new.svg?react';
import PersonIcon from '@material-design-icons/svg/filled/person.svg?react';
import type { ReactNode } from 'react';
import {
  getAutomatedReviewBadgeClassName,
  getAutomatedReviewSummaryLabel,
} from '../../../lib/hubspot-management/automatedReview';
import {
  formatDateTimeWithTimeFirst,
  formatTicketAge,
  getHubspotStatusBadgeColor,
  getHubspotTicketUrl,
  getInitials,
} from '../../../lib/hubspot-management/formatters';
import {
  getHubspotSubmissionKind,
  getHubspotSubmissionKindLabel,
} from '../../../lib/hubspot-management/submissionKind';
import { cn } from '../../../lib/utils';
import type { HubspotTicketDetail } from '../../../stores/hubspotManagementStore';
import { formatFileSize } from '../../../utils/stringHelpers';
import { HubspotDappIcon } from '../HubspotDappIcon';

type TicketLike = Pick<
  HubspotTicketDetail,
  | 'tokenAppName'
  | 'dappName'
  | 'requestType'
  | 'content'
  | 'hasPublishedReleaseHistory'
  | 'mappedSubmissionStatus'
  | 'hsPipelineStage'
  | 'submissionSource'
  | 'tokenVersionName'
  | 'tokenVersionCode'
>;

export function getTicketDisplayName(ticket: TicketLike) {
  return ticket.tokenAppName || ticket.dappName || 'Unknown App';
}

export function getTicketVersionLabel(ticket: TicketLike) {
  const parts = [];

  if (ticket.tokenVersionName) {
    parts.push(`v${ticket.tokenVersionName}`);
  }

  if (ticket.tokenVersionCode) {
    parts.push(`(${ticket.tokenVersionCode})`);
  }

  return parts.length > 0 ? parts.join(' ') : null;
}

export function Chip({
  className,
  title,
  children,
}: {
  className?: string;
  title?: string;
  children: ReactNode;
}) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[11px] font-medium leading-4',
        className,
      )}
    >
      {children}
    </span>
  );
}

export function StatusChip({
  ticket,
  className,
}: {
  ticket: TicketLike;
  className?: string;
}) {
  const status = ticket.mappedSubmissionStatus;

  if (!status) {
    return null;
  }

  return (
    <Chip
      className={cn(
        getHubspotStatusBadgeColor(
          status,
          ticket.hsPipelineStage?.toLowerCase(),
        ),
        className,
      )}
    >
      {status}
    </Chip>
  );
}

export function ChecksChip({
  summary,
  className,
}: {
  summary: HubspotTicketDetail['automatedReviewSummary'];
  className?: string;
}) {
  if (!summary) {
    return (
      <Chip
        className={cn(
          'border-neutral-800 text-neutral-500 border-dashed',
          className,
        )}
        title="No automated review run"
      >
        No checks
      </Chip>
    );
  }

  return (
    <Chip
      className={cn(
        getAutomatedReviewBadgeClassName(summary.effectiveStatus),
        className,
      )}
      title={
        summary.hasOverrides
          ? 'Includes a manual override'
          : 'Automated review result'
      }
    >
      {getAutomatedReviewSummaryLabel(summary)}
      {summary.hasOverrides && (
        <span
          aria-label="Override applied"
          className="size-1.5 rounded-full bg-amber-300"
        />
      )}
    </Chip>
  );
}

export function SubmissionKindChip({
  ticket,
  className,
}: {
  ticket: TicketLike;
  className?: string;
}) {
  const kind = getHubspotSubmissionKind({
    requestType: ticket.requestType,
    content: ticket.content,
    hasPublishedReleaseHistory: ticket.hasPublishedReleaseHistory,
  });

  if (!kind) {
    return null;
  }

  return (
    <Chip
      className={cn(
        kind === 'new'
          ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300'
          : 'border-amber-500/20 bg-amber-500/10 text-amber-300',
        className,
      )}
    >
      {getHubspotSubmissionKindLabel(kind)}
    </Chip>
  );
}

export function SourceChip({
  source,
  className,
}: {
  source: HubspotTicketDetail['submissionSource'];
  className?: string;
}) {
  return (
    <Chip
      className={cn(
        'uppercase tracking-wide',
        source === 'Portal'
          ? 'border-purple-500/20 bg-purple-500/10 text-purple-300'
          : 'border-teal-500/20 bg-teal-500/10 text-teal-300',
        className,
      )}
    >
      {source}
    </Chip>
  );
}

export function AssigneeAvatar({
  name,
  className,
}: {
  name: string | null;
  className?: string;
}) {
  if (!name) {
    return (
      <span
        title="Unassigned"
        aria-label="Unassigned"
        className={cn(
          'inline-flex size-6 shrink-0 items-center justify-center rounded-full border border-dashed border-neutral-600 text-neutral-600',
          className,
        )}
      >
        <PersonIcon className="size-3.5 fill-current" />
      </span>
    );
  }

  return (
    <span
      title={name}
      aria-label={`Assigned to ${name}`}
      className={cn(
        'inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-neutral-700 text-[10px] font-semibold text-neutral-100',
        className,
      )}
    >
      {getInitials(name)}
    </span>
  );
}

export function TicketAge({
  date,
  className,
}: {
  date: Date;
  className?: string;
}) {
  return (
    <time
      dateTime={date.toISOString()}
      title={formatDateTimeWithTimeFirst(date)}
      className={cn('whitespace-nowrap tabular-nums', className)}
    >
      {formatTicketAge(date)}
    </time>
  );
}

/** Icon, name, and the "v1.2 (34) · #12345" meta line. */
export function SubmissionIdentity({
  ticket,
  iconClassName = 'size-10',
}: {
  ticket: HubspotTicketDetail;
  iconClassName?: string;
}) {
  const displayName = getTicketDisplayName(ticket);
  const version = getTicketVersionLabel(ticket);

  return (
    <div className="flex min-w-0 items-center gap-3">
      <HubspotDappIcon
        iconUrl={ticket.tokenIconUrl}
        name={displayName}
        className={iconClassName}
      />
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-white">
          {displayName}
        </div>
        <div className="truncate text-xs text-neutral-400">
          {version && <span>{version}</span>}
          {version && <span className="mx-1 text-neutral-600">·</span>}
          <span className="font-mono">#{ticket.hubspotId}</span>
        </div>
      </div>
    </div>
  );
}

export function HubspotTicketIdCell({
  ticket,
}: {
  ticket: HubspotTicketDetail;
}) {
  return (
    <a
      href={getHubspotTicketUrl(ticket.hubspotId)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => event.stopPropagation()}
      title="Open in HubSpot"
      className="inline-flex items-center gap-1 font-mono text-xs text-teal-400 transition-colors hover:text-teal-300"
    >
      #{ticket.hubspotId}
      <OpenInNewIcon className="size-3 fill-current" />
    </a>
  );
}

export function HubspotAssigneeCell({
  ticket,
}: {
  ticket: HubspotTicketDetail;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <AssigneeAvatar name={ticket.assignedUserName} />
      <span
        className={cn(
          'truncate text-xs',
          ticket.assignedUserName ? 'text-neutral-200' : 'text-neutral-500',
        )}
      >
        {ticket.assignedUserName ?? 'Unassigned'}
      </span>
    </div>
  );
}

export function HubspotVersionAndSizeCell({
  ticket,
}: {
  ticket: HubspotTicketDetail;
}) {
  const version = getTicketVersionLabel(ticket);
  const hasSize = ticket.apkFileSize != null;

  if (!version && !hasSize) {
    return <span className="text-xs text-neutral-500">—</span>;
  }

  return (
    <div className="space-y-0.5">
      {version && <div className="text-xs text-white">{version}</div>}
      {hasSize && (
        <div className="text-[11px] text-neutral-400">
          {formatFileSize(ticket.apkFileSize!)}
        </div>
      )}
    </div>
  );
}

function AddressLink({ address, label }: { address: string; label: string }) {
  return (
    <div>
      <div className="mb-0.5 text-[10px] text-neutral-400">{label}</div>
      <a
        href={`https://explorer.solana.com/address/${address}?cluster=mainnet`}
        target="_blank"
        rel="noopener noreferrer"
        className="block max-w-[180px] truncate font-mono text-xs text-teal-400 transition-colors hover:text-teal-300"
        title={address}
        onClick={(event) => event.stopPropagation()}
      >
        {address.slice(0, 8)}...{address.slice(-6)}
      </a>
    </div>
  );
}

export function HubspotAddressesCell({
  ticket,
}: {
  ticket: HubspotTicketDetail;
}) {
  if (
    !ticket.dappCollectionAccountAddress &&
    !ticket.dappReleaseAccountAddress
  ) {
    return <span className="text-xs text-neutral-500">—</span>;
  }

  return (
    <div className="space-y-1.5">
      {ticket.dappCollectionAccountAddress && (
        <AddressLink
          address={ticket.dappCollectionAccountAddress}
          label="App NFT"
        />
      )}
      {ticket.dappReleaseAccountAddress && (
        <AddressLink
          address={ticket.dappReleaseAccountAddress}
          label="Release NFT"
        />
      )}
    </div>
  );
}

export function HubspotDateCell({
  date,
  className = 'text-white',
}: {
  date: Date | null;
  className?: string;
}) {
  if (!date) {
    return <span className="text-xs text-neutral-500">—</span>;
  }

  return (
    <div className={cn('text-xs', className)}>
      {formatDateTimeWithTimeFirst(date)}
    </div>
  );
}
